import { z } from 'zod';
import { UUID_PATTERN } from './uuid';
/**
 * Chat attachments: user-uploaded documents a chat turn asks the model to read.
 *
 * These are UNTRUSTED USER INPUT, and that is the whole reason this file is
 * separate from the generated-export contracts even though both describe
 * ephemeral objects behind presigned URLs. An export is bytes we produced under
 * the caller's own CASL scope; an attachment is bytes an arbitrary browser
 * pushed into our bucket. The limits below are therefore not ergonomics — they
 * are the trust boundary, and every one of them is re-checked server-side when
 * the file is consumed (extension and reported MIME are never evidence).
 *
 * Uploads are conversation-scoped and ephemeral: the bucket lifecycle rule
 * sweeps the prefix, and they are never tenant artifacts of record.
 */ /**
 * Object-store prefix for chat uploads. Deliberately its own prefix rather than
 * a subfolder of `exports/`: the two carry different retention rules and
 * different trust classes, and a lifecycle rule is written per prefix.
 */ export const CHAT_UPLOAD_STORAGE_PREFIX = 'chat-uploads';
/**
 * What a chat upload may be. Widening this is a product decision, not a config
 * tweak: each type gets its own consume-side screening (a PDF is paged and
 * parsed for text, an image is sniffed and re-encoded), so a type added here
 * without an arm there is a file nothing checks.
 */ export const CHAT_UPLOAD_MEDIA_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg'
];
const CHAT_UPLOAD_MEDIA_TYPE_SET = new Set(CHAT_UPLOAD_MEDIA_TYPES);
/**
 * Narrows a media type a browser or the AI SDK reports as a free string. The
 * transcript's own parts are already typed — this is for the seams that are not.
 */ export function isChatUploadMediaType(value) {
    return value !== undefined && CHAT_UPLOAD_MEDIA_TYPE_SET.has(value);
}
/**
 * The extension a storage key carries for each media type.
 *
 * This mapping is the ONLY thing that decides how a stored object is screened
 * on the way back in: the key is server-minted, so its extension is evidence
 * where the client's reported media type is merely a claim.
 */ export const CHAT_UPLOAD_EXTENSIONS = {
    'application/pdf': 'pdf',
    'image/png': 'png',
    'image/jpeg': 'jpg'
};
/** `(?:pdf|png|jpg)` — the alternation every chat upload key regex shares. */ export const CHAT_UPLOAD_EXTENSION_PATTERN = `(?:${Object.values(CHAT_UPLOAD_EXTENSIONS).join('|')})`;
/** Hard byte ceiling, enforced at presign, by the presigned POST policy, and again at consume. */ export const CHAT_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
/**
 * Hard resolution ceiling for an uploaded image, enforced at consume: the
 * decoded raster is what a normalization costs, and nothing streams it.
 *
 * Chosen from what people actually attach: a 5K or Pro Display XDR screenshot
 * (14.7 and 20.4 MP) and a current iPhone's DEFAULT photo (5712x4284 = 24.5 MP)
 * all pass, which is why the number is not the round 24 MP those phones are
 * sold as. A 48 MP "Max" capture is refused — honestly, as too large, rather
 * than by being called unreadable.
 */ export const MODEL_IMAGE_MAX_PIXELS = 25_000_000;
/** Longest edge a normalized image is resized to fit inside, in pixels. */ export const MODEL_IMAGE_MAX_EDGE = 2000;
/** Under Anthropic's 5 MB per-image ceiling, with room for the encoders' spread. */ export const MODEL_IMAGE_MAX_BYTES = 4 * 1024 * 1024;
/** How long the browser has to complete the direct upload before re-presigning. */ export const CHAT_UPLOAD_PRESIGN_TTL_SECONDS = 900;
/**
 * Hard page ceiling. Only checkable once the bytes exist, so unlike the byte cap
 * it has no presign-time equivalent — an over-long document is rejected at
 * consume and answered with the BLIKS IO Converter pointer.
 */ export const CHAT_UPLOAD_MAX_PAGES = 50;
/**
 * Tokens one PDF page is assumed to cost the model. Deliberately a flat
 * over-estimate: the real cost depends on the provider's own document
 * rasterization, which we cannot measure before sending, and a preflight that
 * under-counts is worse than useless — the request would simply fail at the
 * provider instead of handing off cleanly.
 */ export const CHAT_UPLOAD_ESTIMATED_TOKENS_PER_PAGE = 800;
/**
 * Tokens one image is assumed to cost. Flat rather than derived from the
 * dimensions: every provider tiles differently and we normalise to a bounded
 * box before sending, so a single generous constant is both cheaper and safer
 * than a formula that would be wrong per provider anyway.
 */ export const CHAT_UPLOAD_ESTIMATED_TOKENS_PER_IMAGE = 1500;
/** Bound on the display filename so an unbounded client string never reaches Mongo or a log line. */ export const MAX_CHAT_ATTACHMENT_FILENAME_LENGTH = 255;
/**
 * Attachments one chat turn may carry. Low on purpose: the per-turn token
 * budget, not the count, is the real limit, and a small count keeps a single
 * request from forcing many multi-megabyte downloads before it can be rejected.
 */ export const MAX_CHAT_ATTACHMENTS_PER_MESSAGE = 3;
/**
 * Namespace segment an app-chat upload carries directly under its owner, naming
 * the surface and then the app it was minted for:
 * `chat-uploads/{tenantId}/{userId}/app-chat/{appId}/{uuid}.pdf`.
 *
 * It lives INSIDE the shared prefix on purpose. Retention is a bucket lifecycle
 * rule written per prefix (docs/SERVER_SETUP.md §5.3), so a second top-level
 * prefix would be untrusted user documents that nothing sweeps until an operator
 * adds a rule for it; deeper segments inherit the rule that already exists.
 *
 * The segment is also what keeps the two key shapes apart. Both belong to the
 * same person, so tenant and uploader alone cannot separate them: a legacy
 * (core-Omniscient) key carries no scope segment and an app-chat key carries one
 * naming its app, which makes each unaddressable from the other surface rather
 * than merely forbidden there.
 */ export const APP_CHAT_UPLOAD_SCOPE = 'app-chat';
/**
 * Namespace segment a Bliksy upload carries, naming the surface and then the
 * step recording the chat session belongs to:
 * `chat-uploads/{tenantId}/{userId}/bliksy/{recordingId}/{uuid}.{ext}`.
 *
 * Same reasoning as {@link APP_CHAT_UPLOAD_SCOPE} — inside the swept prefix, and
 * structurally unaddressable from the other surfaces. The RECORDING rather than
 * the session is the axis because a Bliksy session IS a recording, and it is
 * shared: the recorder and an org manager talk to one transcript, so the
 * recording is what a replayed document must still belong to.
 */ export const BLIKSY_UPLOAD_SCOPE = 'bliksy';
/** `chat-uploads/{tenantId}/{userId}` — the owner every chat upload key opens with. */ const CHAT_UPLOAD_OWNER_PATTERN = `${CHAT_UPLOAD_STORAGE_PREFIX}/[A-Za-z0-9_-]{1,64}/[A-Za-z0-9_-]{1,64}`;
/** `app-chat/{appId}` — the surface namespace, absent on a legacy key. */ const APP_CHAT_UPLOAD_SCOPE_PATTERN = `${APP_CHAT_UPLOAD_SCOPE}/[A-Za-z0-9_-]{1,64}`;
/** `bliksy/{recordingId}` — the surface namespace, narrowed to an ObjectId. */ const BLIKSY_UPLOAD_SCOPE_PATTERN = `${BLIKSY_UPLOAD_SCOPE}/[0-9a-f]{24}`;
/** `{uuid}.{ext}` — the object itself, named by an id only we mint. */ const CHAT_UPLOAD_OBJECT_PATTERN = `${UUID_PATTERN}\\.${CHAT_UPLOAD_EXTENSION_PATTERN}`;
/**
 * Exactly the keys the app-chat presign endpoint mints:
 * `chat-uploads/{tenantId}/{userId}/app-chat/{appId}/{uuid}.pdf`.
 *
 * The tenant, the uploader and the app are IN the key, which is what makes the
 * ownership check structural — but this regex only proves the SHAPE. The
 * backend still compares all three against the caller's own verified claims; a
 * well-formed key naming someone else is exactly the attack.
 */ export const APP_CHAT_UPLOAD_KEY_REGEX = new RegExp(`^${CHAT_UPLOAD_OWNER_PATTERN}/${APP_CHAT_UPLOAD_SCOPE_PATTERN}/${CHAT_UPLOAD_OBJECT_PATTERN}$`, 'i');
/**
 * Exactly the keys the Bliksy presign endpoint mints:
 * `chat-uploads/{tenantId}/{userId}/bliksy/{recordingId}/{uuid}.{ext}`.
 *
 * As with the app-chat shape, this proves only the SHAPE — the backend still
 * compares the tenant and the recording against the caller's verified claims
 * and the session it just authorized.
 */ export const BLIKSY_UPLOAD_KEY_REGEX = new RegExp(`^${CHAT_UPLOAD_OWNER_PATTERN}/${BLIKSY_UPLOAD_SCOPE_PATTERN}/${CHAT_UPLOAD_OBJECT_PATTERN}$`, 'i');
/**
 * Every chat upload key shape a PERSISTED file part may name.
 *
 * A stored handle outlives the surface distinction: one transcript reader walks
 * all of them, so the part schema accepts every shape and the surfaces are
 * separated where it matters — at the read that turns a key into bytes. A shape
 * missing from this alternation is not merely rejected: the part fails the
 * persisted-message validator and the transcript reader drops it silently.
 */ export const STORED_CHAT_UPLOAD_KEY_REGEX = new RegExp(`^${CHAT_UPLOAD_OWNER_PATTERN}/(?:${APP_CHAT_UPLOAD_SCOPE_PATTERN}/|${BLIKSY_UPLOAD_SCOPE_PATTERN}/)?${CHAT_UPLOAD_OBJECT_PATTERN}$`, 'i');
/**
 * The display name of an attachment, as a CLIENT supplies it.
 *
 * Two constraints beyond the length bound, both because this string is echoed
 * into a persisted assistant markdown turn (the attachment hand-off) and handed
 * to the model verbatim:
 *  - no Unicode control characters, so a name cannot smuggle newlines or
 *    bidi/format codepoints into that message;
 *  - normalised to its BASENAME, so a path-shaped name is displayed as the file
 *    it names rather than as a location.
 *
 * Markdown-significant characters survive here on purpose — they are legal in a
 * filename — and are escaped where the name is rendered (`escapeMarkdown`).
 */ export const ChatAttachmentFilenameSchema = z.string().min(1).max(MAX_CHAT_ATTACHMENT_FILENAME_LENGTH).regex(/^[^\p{C}]+$/u, 'Filename must not contain control characters').transform(toAttachmentBasename);
function toAttachmentBasename(filename) {
    const basename = filename.split(/[/\\]/).filter(Boolean).pop();
    return basename ?? filename;
}
/**
 * A file the client claims to have uploaded, as a chat request references it.
 *
 * Every field here is a CLAIM, including `size` and `mediaType` — they exist so
 * the request can be rejected cheaply before any bytes are fetched, never as a
 * substitute for inspecting the object. `key` is the only field with authority,
 * and only after the tenant/user segments are matched against the caller.
 *
 * `size` is OPTIONAL because it is only ever a fail-fast: the authoritative
 * checks are the object's `contentLength` and then the downloaded byte length,
 * both server-side. A turn re-sent from history (a `regenerate`) knows the key
 * and the display name — the persisted file part carries no size — so requiring
 * it would mean a retried turn could not name the documents it asked about.
 *
 * There is deliberately no URL field, and there never will be: the model is only
 * ever handed bytes we fetched from a key we minted.
 */ export const ChatAttachmentSchema = z.object({
    // Either surface's shape: the one live consumer
    // ({@link AppChatAttachmentSchema}) narrows this to the app-chat keys it
    // accepts, and the legacy shape survives only in migrated transcripts.
    key: z.string().regex(STORED_CHAT_UPLOAD_KEY_REGEX, 'Invalid chat upload key'),
    filename: ChatAttachmentFilenameSchema,
    mediaType: z.enum(CHAT_UPLOAD_MEDIA_TYPES),
    size: z.number().int().positive().max(CHAT_UPLOAD_MAX_BYTES).optional()
});
/**
 * What the client must declare before it may upload, shared by every chat
 * surface that mints presigned uploads.
 *
 * `size` is the client's own claim and is checked here only to fail fast — the
 * presigned POST policy repeats the ceiling so the object store itself refuses
 * an oversize body, and consume checks the real byte length a third time. A
 * client that lies gains nothing at any of the three.
 *
 * `mediaType` decides the extension of the key we mint and the content type the
 * POST policy pins, and is otherwise not evidence of anything (magic bytes are,
 * at consume — a `.png` key may legally arrive holding JPEG bytes).
 */ export const ChatUploadPresignRequestSchema = z.object({
    filename: ChatAttachmentFilenameSchema,
    mediaType: z.enum(CHAT_UPLOAD_MEDIA_TYPES),
    size: z.number().int().positive().max(CHAT_UPLOAD_MAX_BYTES)
});
/**
 * The presigned POST form the browser submits directly to the object store,
 * plus the key the chat request will later reference.
 *
 * The key is echoed rather than derived client-side on purpose: it is minted
 * server-side from the caller's verified claims, so the client never chooses
 * where its bytes land. Which SHAPE it comes back in is the surface's business
 * (see `APP_CHAT_UPLOAD_SCOPE`); this schema describes the envelope both share.
 */ export const ChatUploadPresignResponseSchema = z.object({
    url: z.string().min(1),
    fields: z.record(z.string(), z.string()),
    key: z.string().regex(STORED_CHAT_UPLOAD_KEY_REGEX),
    expiresIn: z.number().int().positive()
});
/**
 * Why an attachment was not sent to the model. Every value maps to the same
 * product behaviour — a deterministic assistant turn pointing at the Document
 * Analyzer — but they stay distinct so the message can say WHICH limit was hit
 * instead of a generic failure.
 *
 * `unreadable` and `image-only` are deliberately separate: the first is a file
 * we could not parse at all, the second one we parsed perfectly and which simply
 * has no text for a model to read. Both point at the analyzer, but only the
 * second can honestly tell the user their document is a scan.
 *
 * So are the two size ceilings: `too-large` is the byte cap, while a photo that
 * trips {@link MODEL_IMAGE_MAX_PIXELS} can be a few hundred kilobytes, and
 * telling its owner they exceeded 10 MB would send them after a number their
 * file never came close to.
 */ export const CHAT_ATTACHMENT_REJECTION_REASONS = [
    'not-found',
    'too-large',
    'too-large-resolution',
    'too-many-pages',
    'encrypted',
    'unreadable',
    'image-only',
    'token-budget',
    'model-unsupported',
    'busy'
];
export const ChatAttachmentRejectionReasonSchema = z.enum(CHAT_ATTACHMENT_REJECTION_REASONS);
/** Tokens an inspected attachment is budgeted at. */ export function estimateAttachmentTokens(inspection) {
    return inspection.kind === 'image' ? CHAT_UPLOAD_ESTIMATED_TOKENS_PER_IMAGE : inspection.pageCount * CHAT_UPLOAD_ESTIMATED_TOKENS_PER_PAGE;
} /**
 * Expiry is deliberately NOT expressed as an age here. The object store's
 * lifecycle rule on the `chat-uploads/` prefix is the only thing that deletes
 * the bytes, and it is an operator setting per environment — a duplicated
 * day count in code would drift from it silently. The backend therefore
 * decides expiry by PROBING the object: gone means expired, and the transcript
 * renders the stored handle as an expired chip.
 */ 

//# sourceMappingURL=chat-attachment.js.map